import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, FileSpreadsheet, Link2, Loader2, Plug, RefreshCw, Search, Table2, Trash2, ExternalLink, Bell, Mail, Plus, Send, Play, Pause, Upload, X, Bot, Filter, Download } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { UploadDocsButton, ConnectLinkBox, FlotillaJobsSection, HubFilesSection } from "../../components/os/DocsHubExtras";
import { CopilotDock } from "../../components/os/CopilotDock";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const CHART_PALETTE = ["#6C5CE7", "#C9A96A", "#10B981", "#F43F5E", "#0EA5E9", "#0E1A2B", "#F59E0B", "#8B5CF6", "#14B8A6", "#EC4899", "#64748B", "#84CC16"];

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-violet transition-colors";
const timeAgo = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "hace unos segundos";
  if (mins < 60) return `hace ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 31) return `hace ${d} día(s)`;
  if (d < 365) return `hace ${Math.floor(d / 30)} mes(es)`;
  return `hace ${Math.floor(d / 365)} año(s)`;
};
const agoTone = (iso) => {
  if (!iso) return "bg-navy/[0.03] border-navy/10 text-navy/50";
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins < 5) return "bg-emerald-50 border-emerald-200 text-emerald-700";
  if (mins < 60) return "bg-amber-50 border-amber-200 text-amber-700";
  return "bg-navy/[0.03] border-navy/10 text-navy/50";
};
const OP_OPTS = [["gt", "mayor que"], ["lt", "menor que"], ["gte", "mayor o igual a"], ["lte", "menor o igual a"], ["eq", "igual a"], ["contains", "contiene (texto)"]];
const SCOPE_OPTS = [["cell", "Cualquier celda de la columna"], ["sum", "La suma de la columna"], ["avg", "El promedio de la columna"], ["rows", "El número de filas"]];
const PERIOD_OPTS = [["daily", "Diario"], ["weekly", "Semanal"], ["biweekly", "Quincenal (15 días)"], ["monthly", "Mensual"]];

export default function SheetsHub() {
  const [account, setAccount] = useState(null);
  const [connected, setConnected] = useState([]);
  const [available, setAvailable] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    api.get("/sheets/account").then((r) => setAccount(r.data)).catch(() => {});
    api.get("/sheets").then((r) => setConnected(r.data)).catch(() => {});
    // Auto-descubre en vivo: las hojas que ya no están compartidas desaparecen solas de la lista
    api.get("/sheets/discover").then((r) => setAvailable(r.data.filter((f) => !f.connected))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Mantiene la vista siempre al día con cambios externos de Google (sin recargar la página)
  useEffect(() => {
    const id = setInterval(() => {
      api.get("/sheets").then((r) => setConnected(r.data)).catch(() => {});
      api.get("/sheets/discover").then((r) => setAvailable(r.data.filter((f) => !f.connected))).catch(() => {});
    }, 90000);
    return () => clearInterval(id);
  }, []);

  // Tic cada 30 s para que el "Actualizado hace X" se mantenga al día
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const discover = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.get("/sheets/discover");
      setAvailable(data.filter((f) => !f.connected));
      if (!data.length) toast.info("Aún no hay hojas compartidas con el correo de OMNIA");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al buscar hojas"); }
    finally { setDiscovering(false); }
  };

  const connect = async (f) => {
    setBusyId(f.sheet_id);
    try {
      await api.post("/sheets/connect", { sheet_id: f.sheet_id });
      toast.success(`"${f.name}" conectada y sincronizada`);
      setAvailable((av) => (av || []).filter((x) => x.sheet_id !== f.sheet_id));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo conectar"); }
    finally { setBusyId(""); }
  };

  const sync = async (r) => {
    setBusyId(r.sheet_id);
    try { await api.post(`/sheets/${r.sheet_id}/sync`); toast.success("Sincronizada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al sincronizar"); }
    finally { setBusyId(""); }
  };

  const disconnect = async (r) => {
    if (!window.confirm(`¿Desconectar "${r.name}"? OMNIA dejará de leerla (la hoja no se toca).`)) return;
    try { await api.delete(`/sheets/${r.sheet_id}`); toast.success("Desconectada"); setPreview(null); load(); }
    catch { toast.error("Error"); }
  };

  const openPreview = async (r, tab) => {
    try {
      const { data } = await api.get(`/sheets/${r.sheet_id}/data`, { params: { tab: tab || "", limit: 500 } });
      setPreview({ ...data, name: r.name, reg: r });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sin datos"); }
  };

  const uploadExcel = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/sheets/upload-excel", fd);
      toast.success(`"${data.name}" subida — ${(data.tabs || []).length} pestaña(s). Puedes verla y editarla igual que una hoja de Google.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir el archivo"); }
    finally { setUploading(false); }
  };

  const [docsRefresh, setDocsRefresh] = useState(0);
  const onDocsDone = (destino) => { setDocsRefresh((n) => n + 1); if (destino !== "flotilla") load(); };

  const copyEmail = async () => {
    try { await navigator.clipboard.writeText(account?.email || ""); toast.success("Correo copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };

  return (
    <OSLayout title="Documents Hub">
      <div className="space-y-8 p425" data-testid="sheets-hub-page">
        <div className="card-surface p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h3 className="font-medium flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Conecta tus Google Sheets, carpetas de Drive o sube archivos
              </h3>
              <p className="text-sm text-navy/50 mt-1 max-w-2xl">
                Comparte cualquier hoja o carpeta con el correo de OMNIA (permiso Lector es suficiente) o pega su link abajo.
                También puedes subir archivos de cualquier tipo (Excel, PDF, Word, Numbers, Pages…): al subir eliges si
                van a Documents Hub o a OMNIA Flotilla (Strix), donde la IA los interpreta y los carga por unidad.
              </p>
              {account?.email && (
                <button onClick={copyEmail} data-testid="sheets-sa-email"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy text-cream text-xs px-4 py-2 hover:bg-navy-soft transition-colors">
                  {account.email} <Copy className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2 items-stretch">
              <button onClick={discover} disabled={discovering} data-testid="sheets-discover-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50">
                {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar hojas compartidas
              </button>
              <label data-testid="sheets-upload-btn"
                className={`inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet transition-colors cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir Excel editable
                <input type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="sheets-upload-input" onChange={uploadExcel} />
              </label>
              <UploadDocsButton onDone={onDocsDone} />
            </div>
          </div>
          <div className="mt-4 border-t border-navy/5 pt-4">
            <ConnectLinkBox onDone={onDocsDone} />
          </div>
          {available && (
            <div className="mt-5 border-t border-navy/5 pt-4">
              <div className="overline text-navy/40 mb-2">Disponibles para conectar</div>
              {available.length === 0 ? (
                <p className="text-sm text-navy/40">No hay hojas nuevas. Comparte una con el correo de arriba y vuelve a buscar.</p>
              ) : (
                <div className="divide-y divide-navy/5">
                  {available.map((f) => (
                    <div key={f.sheet_id} data-testid={`sheets-avail-${f.sheet_id}`} className="flex items-center gap-3 py-2.5">
                      <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{f.name}</div>
                        <div className="text-[11px] text-navy/40">{f.owner} · {String(f.modified).slice(0, 10)}</div>
                      </div>
                      <button onClick={() => connect(f)} disabled={busyId === f.sheet_id} data-testid={`sheets-connect-${f.sheet_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-violet text-white text-xs px-4 py-2 hover:opacity-90 disabled:opacity-50">
                        {busyId === f.sheet_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Conectar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="overline text-navy/40">Hojas conectadas ({connected.length})</div>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-600" data-testid="sheets-autosync-hint">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Se actualizan solas con los cambios de Google
            </span>
          </div>
          {connected.length === 0 ? (
            <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-8 text-center">
              Sin hojas conectadas aún. Comparte una con el correo de OMNIA y presiona "Buscar hojas compartidas", o sube un Excel desde tu computadora.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {connected.map((r) => {
                const isLocal = r.source === "excel";
                return (
                <div key={r.sheet_id} data-testid={`sheets-card-${r.sheet_id}`} className="card-surface p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        <FileSpreadsheet className={`w-4 h-4 shrink-0 ${isLocal ? "text-amber-600" : "text-emerald-600"}`} /> {r.name}
                        {isLocal && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0" data-testid={`sheets-local-badge-${r.sheet_id}`}>Excel local</span>}
                      </div>
                      <div className="text-[11px] text-navy/40 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{isLocal ? "Subida" : "Sincronizada"} {String(r.last_sync).slice(0, 16).replace("T", " ")} · por {r.connected_by_name}</span>
                        <span className={`inline-flex items-center gap-1 text-[10px] rounded-full border px-2 py-0.5 ${agoTone(r.source_modified || r.last_sync)}`}
                          data-testid={`sheets-updated-${r.sheet_id}`} title={isLocal ? "Última actualización en OMNIA" : "Último cambio detectado en Google"}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                          Actualizado {timeAgo(r.source_modified || r.last_sync)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!isLocal && r.web_link && (
                        <a href={r.web_link} target="_blank" rel="noreferrer" className="text-navy/40 hover:text-navy" title="Abrir en Google">
                          <ExternalLink className="w-4 h-4" /></a>
                      )}
                      {!isLocal && (
                        <button onClick={() => sync(r)} disabled={busyId === r.sheet_id} data-testid={`sheets-sync-${r.sheet_id}`}
                          className="text-navy/40 hover:text-violet disabled:opacity-40" title="Sincronizar ahora">
                          <RefreshCw className={`w-4 h-4 ${busyId === r.sheet_id ? "animate-spin" : ""}`} /></button>
                      )}
                      <button onClick={() => disconnect(r)} data-testid={`sheets-del-${r.sheet_id}`}
                        className="text-navy/30 hover:text-destructive" title={isLocal ? "Eliminar" : "Desconectar"}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(r.tabs || []).map((t) => (
                      <button key={t.tab} onClick={() => openPreview(r, t.tab)} data-testid={`sheets-tab-${r.sheet_id}-${t.tab}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet hover:text-violet transition-colors">
                        <Table2 className="w-3 h-3" /> {t.tab} · {t.row_count}
                      </button>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        <FlotillaJobsSection refreshKey={docsRefresh} />
        <HubFilesSection refreshKey={docsRefresh} />

        {connected.length > 0 && <AlertsSection connected={connected} />}
        {connected.length > 0 && <ReportsSection connected={connected} />}
        <ReportHistory />

        {preview && <SheetViewer preview={preview} onClose={() => setPreview(null)} onRefresh={() => openPreview(preview.reg, preview.tab)} />}
      </div>
      <CopilotDock title="Copiloto · Documents Hub"
        suggestions={["¿Qué dice mi hoja conectada? Resúmela", "Suma las columnas numéricas de la hoja", "Crea una alerta si alguna celda de Stock baja de 10"]} />
    </OSLayout>
  );
}

function SheetViewer({ preview, onClose, onRefresh }) {
  const isLocal = preview.reg?.source === "excel";
  const target = isLocal ? "OMNIA" : "Google Sheets";
  const [rows, setRows] = useState(preview.rows);
  const [editing, setEditing] = useState(null); // {ri, ci, value}
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteMode, setPasteMode] = useState("append"); // append | overwrite | columns
  const [pasteStart, setPasteStart] = useState({ row: 2, col: 0 });
  const [backups, setBackups] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [copOpen, setCopOpen] = useState(false);
  const [copMsgs, setCopMsgs] = useState([]);
  const [copText, setCopText] = useState("");
  const [copBusy, setCopBusy] = useState(false);
  const [copFilter, setCopFilter] = useState(null);
  const [copRows, setCopRows] = useState(new Set());
  const copEndRef = useRef(null);

  useEffect(() => { copEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [copMsgs, copBusy]);

  const askCopilot = async (text) => {
    const msg = String(text ?? copText).trim();
    if (!msg || copBusy) return;
    const history = copMsgs.slice(-8).map((m) => ({ role: m.role, content: m.content }));
    setCopMsgs((m) => [...m, { role: "user", content: msg }]);
    setCopText(""); setCopBusy(true);
    try {
      const { data } = await api.post(`/sheets/${preview.sheet_id}/copilot`, { tab: preview.tab, message: msg, history });
      setCopMsgs((m) => [...m, { role: "assistant", content: data.reply, chart: data.chart }]);
      if (data.filter) setCopFilter(data.filter);
      setCopRows(new Set((data.rows || []).map(Number)));
    } catch (e) {
      setCopMsgs((m) => [...m, { role: "assistant", content: formatApiError(e.response?.data?.detail) || "No pude responder; intenta de nuevo.", error: true }]);
    } finally { setCopBusy(false); }
  };

  useEffect(() => { setRows(preview.rows); }, [preview]);

  const [syncedAt, setSyncedAt] = useState(preview.synced_at);
  useEffect(() => { setSyncedAt(preview.synced_at); }, [preview]);

  // Auto-refresco cada 10 s de la hoja abierta (se pausa mientras editas)
  useEffect(() => {
    const id = setInterval(async () => {
      if (editing || adding || pasteOpen || proposal || saving || aiBusy) return;
      try {
        if (!isLocal) await api.post(`/sheets/${preview.sheet_id}/refresh-if-changed`);
        const { data } = await api.get(`/sheets/${preview.sheet_id}/data`, { params: { tab: preview.tab, limit: 500 } });
        setRows(data.rows);
        setSyncedAt(data.synced_at);
      } catch { /* silencioso */ }
    }, 10000);
    return () => clearInterval(id);
  }, [preview.sheet_id, preview.tab, editing, adding, pasteOpen, proposal, saving, aiBusy, isLocal]);

  const proposeAI = async () => {
    if (!aiText.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post(`/sheets/${preview.sheet_id}/propose-edit`, { tab: preview.tab, instruction: aiText });
      if (!data.changes?.length) { toast.info("La instrucción no produce ningún cambio en los datos actuales"); return; }
      setProposal(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No pude interpretar la instrucción"); }
    finally { setAiBusy(false); }
  };

  const applyProposal = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/sheets/proposals/${proposal.id}/apply`);
      toast.success(`${data.celdas_modificadas} celda(s) modificadas en ${target} — puedes revertir con "Deshacer"`);
      setProposal(null); setAiOpen(false); setAiText(""); onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo aplicar"); }
    finally { setAiBusy(false); }
  };

  const rejectProposal = async () => {
    try { await api.delete(`/sheets/proposals/${proposal.id}`); } catch { /* noop */ }
    setProposal(null);
  };

  const loadBackups = async () => {
    if (backups !== null) { setBackups(null); return; }
    try {
      const { data } = await api.get(`/sheets/${preview.sheet_id}/backups`, { params: { tab: preview.tab } });
      setBackups(data);
    } catch { toast.error("No se pudieron cargar los respaldos"); }
  };

  const restore = async (bk) => {
    if (!window.confirm(`¿Revertir la hoja al respaldo del ${bk.created_at.slice(0, 16).replace("T", " ")} (${bk.row_count} filas, antes de "${bk.action}")?${isLocal ? "" : " Se sobrescribirá la hoja real en Google."}`)) return;
    setSaving(true);
    try {
      await api.post(`/sheets/${preview.sheet_id}/restore/${bk.id}`);
      toast.success("Hoja restaurada al respaldo");
      setBackups(null); onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo restaurar"); }
    finally { setSaving(false); }
  };

  const overwriteBlock = async (startRow, startCol, block) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/sheets/${preview.sheet_id}/update-range`,
        { tab: preview.tab, start_row: startRow, start_col: startCol, rows: block });
      toast.success(`Bloque de ${data.filas} fila(s) × ${data.columnas} columna(s) sobrescrito en ${target}`);
      setEditing(null); setPasteOpen(false); setPasteText(""); onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo sobrescribir"); }
    finally { setSaving(false); }
  };

  const onCellPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!editing || (!text.includes("\t") && !text.includes("\n"))) return;
    e.preventDefault();
    const block = text.replace(/\r/g, "").split("\n").filter((l) => l.length).map((l) => l.split("\t"));
    const cols = Math.max(...block.map((r) => r.length));
    if (window.confirm(`Vas a sobrescribir un bloque de ${block.length} fila(s) × ${cols} columna(s) desde la fila ${editing.ri + 2}, columna "${preview.headers[editing.ci] || editing.ci + 1}". ¿Continuar?`)) {
      overwriteBlock(editing.ri, editing.ci, block);
    } else { setEditing(null); }
  };

  const addColumn = async () => {
    const name = window.prompt("Nombre de la nueva columna:");
    if (!name?.trim()) return;
    setSaving(true);
    try {
      await api.post(`/sheets/${preview.sheet_id}/add-column`, { tab: preview.tab, name });
      toast.success(`Columna "${name}" agregada en ${target}`);
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo agregar la columna"); }
    finally { setSaving(false); }
  };

  const bulkPaste = async () => {
    const parsed = pasteText.replace(/\r/g, "").split("\n").map((l) => l.split("\t")).filter((r) => r.some((c) => c.trim()));
    if (!parsed.length) return;
    if (pasteMode === "overwrite") {
      const startRow = Math.max(0, (Number(pasteStart.row) || 2) - 2);
      return overwriteBlock(startRow, Number(pasteStart.col) || 0, parsed);
    }
    if (pasteMode === "columns") {
      setSaving(true);
      try {
        const { data } = await api.post(`/sheets/${preview.sheet_id}/add-columns-block`, { tab: preview.tab, rows: parsed });
        toast.success(`${data.columnas_agregadas} columna(s) agregadas en ${target}`);
        setPasteOpen(false); setPasteText(""); onRefresh();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron agregar las columnas"); }
      finally { setSaving(false); }
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/sheets/${preview.sheet_id}/bulk`, { tab: preview.tab, rows: parsed });
      toast.success(`${data.added} fila(s) agregadas en ${target}`);
      setPasteOpen(false); setPasteText(""); onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo pegar"); }
    finally { setSaving(false); }
  };

  const saveCell = async () => {
    if (!editing) return;
    const { ri, ci, value } = editing;
    if (String(rows[ri]?.[ci] ?? "") === value) { setEditing(null); return; }
    setSaving(true);
    try {
      await api.post(`/sheets/${preview.sheet_id}/write`, { tab: preview.tab, row: ri, col: ci, value });
      setRows((prev) => prev.map((r, i) => {
        if (i !== ri) return r;
        const copy = [...r];
        while (copy.length <= ci) copy.push("");
        copy[ci] = value;
        return copy;
      }));
      toast.success(`Celda guardada en ${target}`);
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo escribir"); }
    finally { setSaving(false); }
  };

  const addRow = async () => {
    setSaving(true);
    try {
      await api.post(`/sheets/${preview.sheet_id}/write`, { tab: preview.tab, row: -1, values: newRow.map((v) => v || "") });
      toast.success(`Fila agregada en ${target}`);
      setAdding(false); setNewRow([]); onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo agregar"); }
    finally { setSaving(false); }
  };

  const parseNumCell = (s) => { const n = parseFloat(String(s).replace(/[$%\s,]/g, "")); return isNaN(n) ? null : n; };
  const passesCopFilter = (row) => {
    if (!copFilter) return true;
    const ci = preview.headers.indexOf(copFilter.column);
    if (ci < 0) return true;
    const cell = String(row[ci] ?? "");
    const { op, value } = copFilter;
    if (op === "contains") return cell.toLowerCase().includes(String(value).toLowerCase());
    if (op === ">" || op === "<") {
      const n = parseNumCell(cell), v = parseNumCell(value);
      if (n !== null && v !== null) return op === ">" ? n > v : n < v;
    }
    return cell.trim().toLowerCase() === String(value).trim().toLowerCase();
  };
  const visible = rows.map((r, i) => [r, i]).filter(([r]) => passesCopFilter(r))
    .filter(([r]) => !q || r.some((c) => String(c).toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="sheets-viewer">
      <div className={`bg-cream rounded-lg w-full ${copOpen ? "max-w-7xl" : "max-w-6xl"} max-h-[92vh] flex flex-col p-5 gap-3`}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm font-medium truncate">
            {preview.name} · <span className="text-navy/50">{preview.tab}</span>
            <span className="text-navy/40 font-normal"> — {rows.length} filas</span>
            <span className={`ml-2 inline-flex items-center gap-1.5 align-middle text-[10px] font-normal rounded-full border px-2 py-0.5 ${agoTone(syncedAt)}`} data-testid="viewer-updated-ago">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Actualizado {timeAgo(syncedAt)} · en vivo cada 10 s
            </span>
          </div>
          <input className="bg-white border border-navy/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-violet w-48"
            placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="viewer-search" />
          <div className="ml-auto flex items-center gap-2">
            {preview.reg?.web_link && (
              <a href={preview.reg.web_link} target="_blank" rel="noreferrer" className="text-xs text-navy/50 hover:text-violet inline-flex items-center gap-1">
                <ExternalLink className="w-3.5 h-3.5" /> Abrir en Google
              </a>
            )}
            <button onClick={() => { setAdding(!adding); setNewRow([]); }} data-testid="viewer-add-row"
              className="rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Agregar fila
            </button>
            <button onClick={addColumn} disabled={saving} data-testid="viewer-add-column"
              className="rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Agregar columna
            </button>
            <button onClick={() => setPasteOpen(!pasteOpen)} data-testid="viewer-paste"
              className="rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet inline-flex items-center gap-1">
              <Copy className="w-3.5 h-3.5" /> Pegar datos
            </button>
            <button onClick={loadBackups} data-testid="viewer-undo"
              className="rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet inline-flex items-center gap-1">
              <RefreshCw className="w-3.5 h-3.5" /> Deshacer
            </button>
            <button onClick={() => setCopOpen(!copOpen)} data-testid="viewer-copilot-btn"
              className={`rounded-full text-xs px-3 py-1.5 inline-flex items-center gap-1 border transition-colors ${copOpen ? "bg-navy text-cream border-navy" : "border-violet/40 text-violet hover:bg-violet/[0.05]"}`}>
              <Bot className="w-3.5 h-3.5" /> Copiloto
            </button>
            <button onClick={() => setAiOpen(!aiOpen)} data-testid="viewer-ai-edit"
              className="rounded-full bg-gradient-violet text-white text-xs px-3 py-1.5 hover:opacity-90 inline-flex items-center gap-1">
              ✨ Pedir cambio a la IA
            </button>
            <button onClick={onClose} className="text-navy/40 hover:text-navy" data-testid="viewer-close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {pasteOpen && (
          <div className="rounded-lg border border-violet/30 bg-violet/[0.03] p-3 space-y-2" data-testid="viewer-paste-panel">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="radio" checked={pasteMode === "append"} onChange={() => setPasteMode("append")} data-testid="paste-mode-append" />
                Agregar al final
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="radio" checked={pasteMode === "overwrite"} onChange={() => setPasteMode("overwrite")} data-testid="paste-mode-overwrite" />
                Sobrescribir celdas existentes
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="radio" checked={pasteMode === "columns"} onChange={() => setPasteMode("columns")} data-testid="paste-mode-columns" />
                Agregar como columnas
              </label>
              {pasteMode === "overwrite" && (
                <span className="inline-flex items-center gap-2">
                  desde fila
                  <input type="number" min={2} className="w-16 bg-white border border-navy/10 rounded px-2 py-1 outline-none focus:border-violet"
                    value={pasteStart.row} onChange={(e) => setPasteStart({ ...pasteStart, row: e.target.value })} data-testid="paste-start-row" />
                  columna
                  <select className="bg-white border border-navy/10 rounded px-2 py-1 outline-none" value={pasteStart.col}
                    onChange={(e) => setPasteStart({ ...pasteStart, col: Number(e.target.value) })} data-testid="paste-start-col">
                    {preview.headers.map((h, i) => <option key={i} value={i}>{h || `Col ${i + 1}`}</option>)}
                  </select>
                </span>
              )}
            </div>
            <p className="text-xs text-navy/50">
              {pasteMode === "append"
                ? "Copia filas y columnas desde Excel o Google Sheets y pégalas aquí (separadas por tabulador). Se agregarán al FINAL de la hoja real."
                : pasteMode === "columns"
                  ? "El bloque se agregará como COLUMNAS nuevas a la derecha de la hoja: la primera línea del pegado son los nombres de las columnas y las siguientes los valores (alineados a las filas existentes desde la fila 2)."
                  : "El bloque pegado SOBRESCRIBIRÁ las celdas existentes a partir de la fila y columna elegidas — directo en la hoja real. Tip: también puedes hacer clic en una celda y pegar (Ctrl+V) un bloque ahí mismo."}
            </p>
            <textarea className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-xs font-mono outline-none focus:border-violet" rows={4}
              value={pasteText} onChange={(e) => setPasteText(e.target.value)} data-testid="viewer-paste-textarea"
              placeholder={"Juan\tMale\t1. Freshman\tCA\nAna\tFemale\t2. Sophomore\tTX"} />
            <button onClick={bulkPaste} disabled={saving || !pasteText.trim()} data-testid="viewer-paste-submit"
              className="rounded-full bg-gradient-violet text-white text-xs px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {pasteMode === "append" ? "Agregar" : pasteMode === "columns" ? "Agregar como columnas" : "Sobrescribir"} {pasteText.split("\n").filter((l) => l.trim()).length || 0} línea(s) en Google Sheets
            </button>
          </div>
        )}

        {aiOpen && !proposal && (
          <div className="rounded-lg border border-violet/30 bg-violet/[0.03] p-3 space-y-2" data-testid="viewer-ai-panel">
            <p className="text-xs text-navy/50">Dile a la IA qué cambiar — te mostrará la propuesta ANTES de escribir nada. Ej.: "súbele 10% a los precios", "pon Pendiente en Estado donde Stock sea 0", "reemplaza CDMX por Ciudad de México".</p>
            <div className="flex gap-2">
              <input className="flex-1 bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet"
                value={aiText} onChange={(e) => setAiText(e.target.value)} data-testid="viewer-ai-input"
                placeholder="¿Qué quieres cambiar en esta hoja?" onKeyDown={(e) => e.key === "Enter" && proposeAI()} />
              <button onClick={proposeAI} disabled={aiBusy || !aiText.trim()} data-testid="viewer-ai-propose"
                className="rounded-full bg-gradient-violet text-white text-xs px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "✨"} Proponer cambios
              </button>
            </div>
          </div>
        )}

        {proposal && (
          <div className="rounded-lg border border-violet bg-violet/[0.04] p-4 space-y-3" data-testid="viewer-ai-proposal">
            <div className="text-sm"><b>Propuesta de la IA:</b> {proposal.resumen}</div>
            <div className="text-xs text-navy/50">"{proposal.instruction}" → <b>{proposal.changes.length} celda(s)</b> en la columna "{proposal.operation?.column}". Nada se escribe hasta que confirmes; podrás revertir con "Deshacer".</div>
            <div className="max-h-44 overflow-auto border border-navy/10 rounded-lg bg-white">
              <table className="w-full text-xs">
                <thead className="bg-navy/[0.03] sticky top-0"><tr>
                  <th className="text-left px-3 py-1.5 font-medium">Fila</th>
                  <th className="text-left px-3 py-1.5 font-medium">Antes</th>
                  <th className="text-left px-3 py-1.5 font-medium">Después</th></tr></thead>
                <tbody className="divide-y divide-navy/5">
                  {proposal.changes.slice(0, 30).map((c, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1 text-navy/40">{c.row + 2}</td>
                      <td className="px-3 py-1 text-red-600 line-through">{c.before}</td>
                      <td className="px-3 py-1 text-emerald-700 font-medium">{c.after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {proposal.changes.length > 30 && <p className="px-3 py-1.5 text-[11px] text-navy/40">… y {proposal.changes.length - 30} cambio(s) más</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={applyProposal} disabled={aiBusy} data-testid="viewer-ai-apply"
                className="rounded-full bg-gradient-violet text-white text-xs px-5 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "✓"} {isLocal ? "Confirmar y aplicar" : "Confirmar y escribir en Google Sheets"}
              </button>
              <button onClick={rejectProposal} disabled={aiBusy} data-testid="viewer-ai-reject"
                className="rounded-full border border-navy/15 text-xs px-4 py-2 hover:border-destructive hover:text-destructive">Rechazar</button>
            </div>
          </div>
        )}

        {backups !== null && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-1.5" data-testid="viewer-backups">
            <div className="text-xs font-medium text-navy/70">Respaldos automáticos — se guarda uno antes de cada cambio (máx. 10)</div>
            {backups.length === 0 ? (
              <p className="text-xs text-navy/40">Aún no hay respaldos: se crearán en cuanto edites, pegues o sobrescribas algo.</p>
            ) : backups.map((bk) => (
              <div key={bk.id} className="flex items-center gap-3 text-xs" data-testid={`backup-${bk.id}`}>
                <span className="text-navy/60">{bk.created_at.slice(0, 16).replace("T", " ")}</span>
                <span className="text-navy/40">antes de: {bk.action}</span>
                <span className="text-navy/40">{bk.row_count} filas</span>
                <span className="text-navy/30">{bk.created_by}</span>
                <button onClick={() => restore(bk)} disabled={saving} data-testid={`restore-${bk.id}`}
                  className="ml-auto rounded-full bg-navy text-cream px-3 py-1 hover:bg-navy-soft disabled:opacity-50">Restaurar</button>
              </div>
            ))}
          </div>
        )}

        {copFilter && (
          <div className="flex items-center gap-2 text-xs" data-testid="viewer-copilot-filter">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-violet/10 text-violet px-3 py-1">
              <Filter className="w-3 h-3" /> Filtro del copiloto: <b>{copFilter.column}</b> {copFilter.op === "contains" ? "contiene" : copFilter.op} "{String(copFilter.value)}" — {visible.length} fila(s)
              <button onClick={() => setCopFilter(null)} className="ml-1 hover:text-navy" data-testid="viewer-copilot-filter-clear"><X className="w-3 h-3" /></button>
            </span>
          </div>
        )}

        <div className="flex gap-3 flex-1 min-h-0">
        <div className="overflow-auto border border-navy/10 rounded-lg bg-white flex-1">
          <table className="w-full text-xs">
            <thead className="bg-navy/[0.03] sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-navy/30 font-normal">#</th>
                {preview.headers.map((h, i) => <th key={i} className="text-left px-3 py-2 font-medium whitespace-nowrap bg-navy/[0.03]">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {adding && (
                <tr className="bg-violet/5">
                  <td className="px-2 py-1 text-navy/30">+</td>
                  {preview.headers.map((_, ci) => (
                    <td key={ci} className="px-1 py-1">
                      <input className="w-full min-w-[80px] bg-white border border-violet/30 rounded px-2 py-1 text-xs outline-none focus:border-violet"
                        value={newRow[ci] || ""} data-testid={`viewer-new-cell-${ci}`}
                        onChange={(e) => setNewRow((prev) => { const c = [...prev]; c[ci] = e.target.value; return c; })} />
                    </td>
                  ))}
                </tr>
              )}
              {visible.map(([row, ri]) => (
                <tr key={ri} className={copRows.has(ri + 2) ? "bg-violet/[0.06]" : "hover:bg-navy/[0.02]"}>
                  <td className="px-2 py-1.5 text-navy/30">{ri + 2}</td>
                  {preview.headers.map((_, ci) => (
                    <td key={ci} className="px-1 py-0.5 whitespace-nowrap cursor-pointer" data-testid={`viewer-cell-${ri}-${ci}`}
                      onClick={() => !saving && setEditing({ ri, ci, value: String(row[ci] ?? "") })}>
                      {editing && editing.ri === ri && editing.ci === ci ? (
                        <input autoFocus className="w-full min-w-[80px] bg-white border border-violet rounded px-2 py-1 text-xs outline-none"
                          value={editing.value} data-testid="viewer-cell-input"
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onPaste={onCellPaste}
                          onBlur={saveCell}
                          onKeyDown={(e) => { if (e.key === "Enter") saveCell(); if (e.key === "Escape") setEditing(null); }} />
                      ) : (
                        <span className="block px-2 py-1 rounded hover:bg-violet/5">{row[ci] ?? ""}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {copOpen && (
          <div className="w-80 shrink-0 border border-violet/25 rounded-lg bg-white flex flex-col min-h-0" data-testid="viewer-copilot-panel">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-navy/10">
              <Bot className="w-4 h-4 text-violet" />
              <span className="text-xs font-medium">Copiloto · {preview.name}</span>
              <button onClick={() => setCopOpen(false)} className="ml-auto text-navy/40 hover:text-navy" data-testid="viewer-copilot-close"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-2.5" data-testid="viewer-copilot-messages">
              {copMsgs.length === 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-navy/50">Pregúntale lo que quieras sobre esta hoja: análisis, resúmenes, conteos o filtros — sin salir de la plantilla.</p>
                  {["Resume esta hoja", "Filtra los de prioridad Alta", "Haz una gráfica de pastel por área", "¿Cuántos siguen En proceso y de qué áreas?"].map((s) => (
                    <button key={s} onClick={() => askCopilot(s)} data-testid="viewer-copilot-suggestion"
                      className="block w-full text-left text-xs rounded-lg border border-navy/10 px-3 py-2 hover:border-violet hover:text-violet transition-colors">{s}</button>
                  ))}
                </div>
              )}
              {copMsgs.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex"}>
                  <div className={`${m.chart ? "w-full" : "max-w-[90%]"} rounded-xl px-3 py-2 text-xs whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-navy text-cream" : m.error ? "bg-red-50 text-red-700 border border-red-100" : "bg-violet/[0.06] text-navy/85"}`}>
                    {m.content}
                    {m.chart && <CopilotChart chart={m.chart} />}
                  </div>
                </div>
              ))}
              {copBusy && <div className="flex items-center gap-2 text-xs text-navy/40"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizando la hoja…</div>}
              <div ref={copEndRef} />
            </div>
            <div className="p-2.5 border-t border-navy/10 flex gap-2">
              <input className="flex-1 bg-white border border-navy/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-violet"
                value={copText} onChange={(e) => setCopText(e.target.value)} data-testid="viewer-copilot-input"
                placeholder="Ej.: filtra por Tesorería, analiza los atrasos…"
                onKeyDown={(e) => e.key === "Enter" && askCopilot()} />
              <button onClick={() => askCopilot()} disabled={copBusy || !copText.trim()} data-testid="viewer-copilot-send"
                className="rounded-full bg-gradient-violet text-white px-3 py-2 hover:opacity-90 disabled:opacity-50">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {adding && (
            <button onClick={addRow} disabled={saving} data-testid="viewer-save-row"
              className="rounded-full bg-gradient-violet text-white text-xs px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Guardar fila en Google Sheets
            </button>
          )}
          {Object.entries(preview.numeric_totals || {}).slice(0, 5).map(([k, v]) => (
            <span key={k} className="badge badge-muted text-[11px]">{k}: suma {v.sum.toLocaleString()} · prom {v.avg.toLocaleString()}</span>
          ))}
          <span className="text-[11px] text-navy/40 ml-auto">{isLocal ? "Haz clic en cualquier celda para editarla — los cambios se guardan en OMNIA (Excel local)." : "Haz clic en cualquier celda para editarla — se guarda directo en Google Sheets (requiere permiso Editor para el correo de OMNIA)."}</span>
        </div>
      </div>
    </div>
  );
}

function CopilotChart({ chart }) {
  const data = chart.data || [];
  const chartRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const short = (s) => (String(s).length > 10 ? String(s).slice(0, 9) + "…" : String(s));
  const downloadPng = async () => {
    if (!chartRef.current || exporting) return;
    setExporting(true);
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(chartRef.current, { backgroundColor: "#ffffff", pixelRatio: 3, filter: (n) => !(n.dataset && n.dataset.noExport) });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${(chart.title || "grafica").replace(/[^\wáéíóúñÁÉÍÓÚÑ -]/g, "").trim().replace(/\s+/g, "_") || "grafica"}.png`;
      a.click();
      toast.success("Gráfica descargada como imagen (PNG)");
    } catch { toast.error("No se pudo exportar la gráfica"); }
    finally { setExporting(false); }
  };
  return (
    <div ref={chartRef} className="mt-2 rounded-lg border border-navy/10 bg-white p-2 relative" data-testid="copilot-chart">
      <button onClick={downloadPng} disabled={exporting} data-no-export="1" data-testid="copilot-chart-download"
        title="Descargar como imagen PNG"
        className="absolute top-1.5 right-1.5 z-10 rounded-full border border-navy/10 bg-white p-1.5 text-navy/40 hover:text-violet hover:border-violet transition-colors">
        {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      </button>
      {chart.title && <div className="text-[11px] font-medium text-navy/70 mb-1 text-center">{chart.title}</div>}
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === "pie" ? (
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={62} innerRadius={30} paddingAngle={2} stroke="none">
                {data.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </PieChart>
          ) : chart.type === "line" ? (
            <LineChart data={data} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9 }} tickFormatter={short} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Line type="monotone" dataKey="value" stroke="#6C5CE7" strokeWidth={2} dot={{ r: 2.5 }} />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9 }} tickFormatter={short} interval={0} angle={data.length > 5 ? -30 : 0} textAnchor={data.length > 5 ? "end" : "middle"} height={data.length > 5 ? 42 : 24} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} cursor={{ fill: "rgba(108,92,231,0.06)" }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {chart.type === "pie" && (
        <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 justify-center mt-1">
          {data.map((d, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[10px] text-navy/60">
              <span className="w-2 h-2 rounded-full" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
              {d.label} ({d.value.toLocaleString()})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function AlertsSection({ connected }) {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ sheet_id: "", tab: "", name: "", scope: "cell", column: "", op: "gt", value: "", notify_email: false });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/sheets/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const reg = connected.find((c) => c.sheet_id === f.sheet_id);
  const tabs = reg?.tabs || [];
  const headers = (tabs.find((t) => t.tab === f.tab) || tabs[0])?.headers || [];

  const create = async (e) => {
    e.preventDefault();
    if (!f.sheet_id || !f.value) { toast.error("Completa hoja y valor"); return; }
    setBusy(true);
    try {
      await api.post("/sheets/alerts", { ...f, tab: f.tab || tabs[0]?.tab || "" });
      toast.success("Alerta creada — se evalúa en cada sincronización");
      setOpen(false); setF({ sheet_id: "", tab: "", name: "", scope: "cell", column: "", op: "gt", value: "", notify_email: false }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };
  const test = async (a) => {
    try {
      const { data } = await api.post(`/sheets/alerts/${a.id}/test`);
      (data.triggered ? toast.warning : toast.success)(`${data.triggered ? "SE DISPARA" : "OK"}: ${data.detail}`);
    } catch { toast.error("Error al probar"); }
  };
  const toggle = async (a) => { try { await api.patch(`/sheets/alerts/${a.id}`, { active: !a.active }); load(); } catch { toast.error("Error"); } };
  const del = async (a) => {
    if (!window.confirm(`¿Eliminar la alerta "${a.name || a.sheet_name}"?`)) return;
    try { await api.delete(`/sheets/alerts/${a.id}`); load(); } catch { toast.error("Error"); }
  };

  return (
    <div data-testid="sheets-alerts-section">
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-navy/40">Alertas por umbral ({alerts.length})</div>
        <button onClick={() => setOpen(true)} data-testid="sheets-new-alert"
          className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2 hover:border-violet hover:text-violet transition-colors">
          <Bell className="w-4 h-4" /> Nueva alerta
        </button>
      </div>
      {alerts.length === 0 ? (
        <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-6 text-center">
          Ej.: "avísame si la suma de Total MXN supera 500,000" o "si alguna celda de Stock baja de 10".
        </div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
          {alerts.map((a) => (
            <div key={a.id} data-testid={`sheets-alert-${a.id}`} className="flex items-center gap-3 px-4 py-3">
              <span className={`w-2 h-2 rounded-full shrink-0 ${a.last_state === "triggered" ? "bg-red-500" : "bg-emerald-500"}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{a.name || `${a.sheet_name} · ${a.tab}`}</div>
                <div className="text-[11px] text-navy/40 truncate">
                  {a.sheet_name} · {a.tab} — {a.last_detail || `${a.scope === "rows" ? "filas" : a.column} ${a.op} ${a.value}`}
                  {a.notify_email ? " · 📧" : ""}{!a.active ? " · pausada" : ""}
                </div>
              </div>
              <button onClick={() => test(a)} className="text-navy/40 hover:text-violet" title="Probar ahora" data-testid={`sheets-alert-test-${a.id}`}><Play className="w-4 h-4" /></button>
              <button onClick={() => toggle(a)} className="text-navy/40 hover:text-navy" title={a.active ? "Pausar" : "Activar"}>{a.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
              <button onClick={() => del(a)} className="text-navy/30 hover:text-destructive" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="sheets-alert-modal">
          <form onSubmit={create} className="bg-cream rounded-lg p-6 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between"><h3 className="font-medium">Nueva alerta</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy">✕</button></div>
            <select className={inputCls} value={f.sheet_id} data-testid="alert-sheet"
              onChange={(e) => setF({ ...f, sheet_id: e.target.value, tab: "", column: "" })}>
              <option value="">Hoja…</option>
              {connected.map((c) => <option key={c.sheet_id} value={c.sheet_id}>{c.name}</option>)}
            </select>
            {tabs.length > 1 && (
              <select className={inputCls} value={f.tab} onChange={(e) => setF({ ...f, tab: e.target.value, column: "" })}>
                {tabs.map((t) => <option key={t.tab} value={t.tab}>{t.tab}</option>)}
              </select>
            )}
            <select className={inputCls} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid="alert-scope">
              {SCOPE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {f.scope !== "rows" && (
              <select className={inputCls} value={f.column} onChange={(e) => setF({ ...f, column: e.target.value })} data-testid="alert-column">
                <option value="">Columna…</option>
                {headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            )}
            <div className="flex gap-2">
              <select className={inputCls} value={f.op} onChange={(e) => setF({ ...f, op: e.target.value })} data-testid="alert-op">
                {OP_OPTS.filter(([v]) => v !== "contains" || f.scope === "cell").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input className={inputCls} placeholder="Valor umbral" value={f.value} data-testid="alert-value"
                onChange={(e) => setF({ ...f, value: e.target.value })} />
            </div>
            <input className={inputCls} placeholder="Nombre de la alerta (opcional)" value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-navy/60">
              <input type="checkbox" checked={f.notify_email} onChange={(e) => setF({ ...f, notify_email: e.target.checked })} />
              Enviarme también un correo al dispararse
            </label>
            <button disabled={busy} data-testid="alert-submit"
              className="w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Crear alerta
            </button>
            <p className="text-[11px] text-navy/40">Las alertas se evalúan automáticamente cada vez que la hoja cambia (revisión cada 5 min) y solo avisan al cruzar el umbral.</p>
          </form>
        </div>
      )}
    </div>
  );
}

function ReportsSection({ connected }) {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email_to: "", sheet_ids: [], mode: "resumen", instructions: "", period: "weekly" });
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState("");
  const load = useCallback(() => { api.get("/sheets/reports").then((r) => setReports(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.email && !f.email_to) setF((p) => ({ ...p, email_to: user.email })); }, [user]); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sheets/reports", f);
      toast.success("Informe programado");
      setOpen(false); setF({ name: "", email_to: user?.email || "", sheet_ids: [], mode: "resumen", instructions: "", period: "weekly" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };
  const sendNow = async (r) => {
    setSendingId(r.id);
    try {
      const { data } = await api.post(`/sheets/reports/${r.id}/send-now`);
      data.sent ? toast.success(`Informe enviado a ${r.email_to}`) : toast.error(`No se envió: ${data.status} ${data.detail || ""}`);
      load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error"); }
    finally { setSendingId(""); }
  };
  const toggle = async (r) => { try { await api.patch(`/sheets/reports/${r.id}`, { active: !r.active }); load(); } catch { toast.error("Error"); } };
  const del = async (r) => {
    if (!window.confirm(`¿Eliminar el informe "${r.name}"?`)) return;
    try { await api.delete(`/sheets/reports/${r.id}`); load(); } catch { toast.error("Error"); }
  };
  const toggleSheet = (id) => setF((p) => ({ ...p, sheet_ids: p.sheet_ids.includes(id) ? p.sheet_ids.filter((x) => x !== id) : [...p.sheet_ids, id] }));

  return (
    <div data-testid="sheets-reports-section">
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-navy/40">Informes por correo ({reports.length})</div>
        <button onClick={() => setOpen(true)} data-testid="sheets-new-report"
          className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2 hover:border-violet hover:text-violet transition-colors">
          <Plus className="w-4 h-4" /> Nuevo informe
        </button>
      </div>
      {reports.length === 0 ? (
        <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-6 text-center">
          Programa todos los que quieras: resumen general o una petición específica, diario, semanal, quincenal o mensual.
        </div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
          {reports.map((r) => (
            <div key={r.id} data-testid={`sheets-report-${r.id}`} className="flex items-center gap-3 px-4 py-3">
              <Mail className="w-4 h-4 text-violet shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.name}</div>
                <div className="text-[11px] text-navy/40 truncate">
                  {PERIOD_OPTS.find(([v]) => v === r.period)?.[1]} · {r.sheet_ids?.length ? `${r.sheet_ids.length} hoja(s)` : "todas las hojas"} · {r.mode === "personalizado" ? "petición específica" : "resumen"} → {r.email_to}
                  {r.last_sent ? ` · último: ${String(r.last_sent).slice(0, 16).replace("T", " ")}` : " · aún no enviado"}{!r.active ? " · pausado" : ""}
                </div>
              </div>
              <button onClick={() => sendNow(r)} disabled={sendingId === r.id} data-testid={`sheets-report-send-${r.id}`}
                title="Sincroniza las hojas y envía el informe al momento"
                className="inline-flex items-center gap-1.5 rounded-full bg-gradient-violet text-white text-xs px-3.5 py-1.5 hover:opacity-90 disabled:opacity-50 shrink-0">
                {sendingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Enviar ahora
              </button>
              <button onClick={() => toggle(r)} className="text-navy/40 hover:text-navy" title={r.active ? "Pausar" : "Activar"}>{r.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
              <button onClick={() => del(r)} className="text-navy/30 hover:text-destructive" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="sheets-report-modal">
          <form onSubmit={create} className="bg-cream rounded-lg p-6 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between"><h3 className="font-medium">Nuevo informe por correo</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy">✕</button></div>
            <input className={inputCls} placeholder="Nombre del informe" value={f.name} required data-testid="report-name"
              onChange={(e) => setF({ ...f, name: e.target.value })} />
            <input className={inputCls} placeholder="Correo destino" type="email" value={f.email_to} required data-testid="report-email"
              onChange={(e) => setF({ ...f, email_to: e.target.value })} />
            <select className={inputCls} value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="report-period">
              {PERIOD_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="text-xs text-navy/50">Hojas incluidas (ninguna marcada = todas):</div>
            <div className="flex flex-wrap gap-1.5">
              {connected.map((c) => (
                <button type="button" key={c.sheet_id} onClick={() => toggleSheet(c.sheet_id)}
                  className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${f.sheet_ids.includes(c.sheet_id) ? "bg-navy text-cream border-navy" : "border-navy/15 text-navy/60"}`}>
                  {c.name}
                </button>
              ))}
            </div>
            <select className={inputCls} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="report-mode">
              <option value="resumen">Resumen general (lo más relevante + evolución en el periodo)</option>
              <option value="personalizado">Petición específica (yo indico qué analizar)</option>
            </select>
            {f.mode === "personalizado" && (
              <textarea className={inputCls} rows={3} data-testid="report-instructions"
                placeholder='Ej.: "Analiza la evolución de la columna Total MXN por sucursal y señala caídas mayores al 10%"'
                value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} />
            )}
            <button disabled={busy} data-testid="report-submit"
              className="w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Programar informe
            </button>
            <p className="text-[11px] text-navy/40">Puedes crear todos los informes que quieras, cada uno con hojas, enfoque y frecuencia distintos. Usa "Enviar ahora" para probarlo al momento.</p>
          </form>
        </div>
      )}
    </div>
  );
}

export function ReportHistory({ compact = false }) {
  const [runs, setRuns] = useState([]);
  const [view, setView] = useState(null);
  const load = useCallback(() => { api.get("/sheets/report-runs").then((r) => setRuns(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (run) => {
    try { const { data } = await api.get(`/sheets/report-runs/${run.id}`); setView(data); }
    catch { toast.error("No se pudo abrir el informe"); }
  };
  const del = async (run) => {
    if (!window.confirm(`¿Eliminar del histórico "${run.report_name}" (${String(run.created_at).slice(0, 10)})?`)) return;
    try { await api.delete(`/sheets/report-runs/${run.id}`); load(); } catch { toast.error("Error"); }
  };

  if (compact && runs.length === 0) return null;
  return (
    <div data-testid="sheets-report-history">
      {!compact && <div className="overline text-navy/40 mb-3">Histórico de informes generados ({runs.length})</div>}
      {runs.length === 0 ? (
        <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-6 text-center">
          Aquí quedará guardado cada informe generado (además del correo), hasta que tú lo borres.
        </div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
          {(compact ? runs.slice(0, 3) : runs).map((run) => (
            <div key={run.id} data-testid={`report-run-${run.id}`} className="flex items-center gap-3 px-4 py-3">
              <FileSpreadsheet className="w-4 h-4 text-violet shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{run.report_name}</div>
                <div className="text-[11px] text-navy/40 truncate">
                  {String(run.created_at).slice(0, 16).replace("T", " ")} · {run.email_to} · {run.status === "success" ? "enviado ✓" : run.status}
                </div>
              </div>
              <button onClick={() => open(run)} data-testid={`report-run-view-${run.id}`}
                className="text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors">Ver</button>
              <button onClick={() => del(run)} className="text-navy/30 hover:text-destructive" title="Eliminar del histórico"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {view && (
        <div className="fixed inset-0 z-[80] bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="report-run-modal">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-navy/10">
              <div className="text-sm font-medium truncate">{view.report_name} · {String(view.created_at).slice(0, 16).replace("T", " ")}</div>
              <button onClick={() => setView(null)} data-testid="report-run-close" className="text-navy/40 hover:text-navy">✕</button>
            </div>
            <div className="overflow-y-auto p-5" dangerouslySetInnerHTML={{ __html: view.html }} />
          </div>
        </div>
      )}
    </div>
  );
}
